/**
 * Who may SEE a keybook item's access list and exposure (plan
 * docs/plans/2026-10-15_KEYBOOK_GRANT_HIERARCHY.md §6, D25/D26) — the ONE gate
 * for the access GETs of a key and a collection, the exposure GET and the UI
 * (the Access menu item, the exposure line and dialog).
 *
 * Seeing is never more than the item itself: someone who can't see a key or a
 * collection (No access) sees nothing about it. Then:
 *  - **Manage** on the item → the FULL access list (and every control).
 *  - **Write** on the item → the read-only list (D25/D26: Write counts as
 *    `manage_keybook_permission:read` for that item).
 *  - **Read** on the item → the read-only list only when the tenant-wide
 *    capability `manage_keybook_permission` resolves ≥ read for them.
 * The capability never grants AUTHORITY: changing access still needs Manage on
 * the item, through the judge — `write` at the capability is not Manage.
 *
 * Browser-safe (the client asks the same question to decide what to show).
 */
import { capAtLeast, type CapValue } from '@papan/types'

export type KeybookItemLevel = 'none' | 'read' | 'write' | 'manage'
export type KeybookAccessView = 'full' | 'filtered'

/** `itemLevel` — the viewer's resolved level on the key / collection (null = none);
 *  `capability` — their tenant-wide `manage_keybook_permission` (resolved once). */
export function keybookAccessView(itemLevel: KeybookItemLevel | null | undefined, capability: CapValue | null | undefined): KeybookAccessView | null {
  if (!itemLevel || itemLevel === 'none') return null
  if (itemLevel === 'manage') return 'full'
  if (itemLevel === 'write') return 'filtered'
  return capability != null && capAtLeast(capability, 'read', 'access') ? 'filtered' : null
}

/** May this viewer see the item's access list and exposure at all? */
export function canSeeKeybookAccess(itemLevel: KeybookItemLevel | null | undefined, capability: CapValue | null | undefined): boolean {
  return keybookAccessView(itemLevel, capability) !== null
}

/** The read-only view of grant rows: "No access" rows hidden, nothing but the
 *  recipient and level (no granter, no dates). */
export function readOnlyGrantRows<R extends { recipientType: string; recipientId: string; permission: string }>(
  rows: readonly R[],
): Array<{ recipientType: string; recipientId: string; permission: string }> {
  return rows.filter(r => r.permission !== 'none').map(r => ({ recipientType: r.recipientType, recipientId: r.recipientId, permission: r.permission }))
}
